import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, formatAge, formatDateBR, formatDateTimeBR, saveEditedImage } from '../api'
import type { Equipment, Phrase, Professional, ReportDetail, ReportTemplate, User } from '../api'
import { MediaImage } from '../components/MediaImage'
import { MediaViewer } from '../components/MediaViewer'
import { ImageEditor } from '../components/ImageEditor'
import { EnviarLaudo } from './EnviarLaudo'

// Editor de laudo: tópicos do modelo, frases-padrão, galeria de imagens com
// legendas, versões e liberação (somente leitura depois).
export function Laudo({ reportId, user, onBack, onOpenPreview }: {
  reportId: string
  user: User
  onBack: () => void
  onOpenPreview: (reportId: string) => void
}) {
  const [detail, setDetail] = useState<ReportDetail | null>(null)
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [content, setContent] = useState<Record<string, string>>({})
  const [activeTopic, setActiveTopic] = useState('')
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const [viewing, setViewing] = useState<number | null>(null)
  const [editing, setEditing] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  const [showFrases, setShowFrases] = useState(false)
  const [phrases, setPhrases] = useState<Phrase[]>([])
  const [examinerId, setExaminerId] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [professionals, setProfessionals] = useState<Professional[]>([])
  const [equipments, setEquipments] = useState<Equipment[]>([])

  const released = detail?.report.status === 'liberado'
  const canWrite = user.role === 'admin' || user.role === 'medico'
  const canEdit = canWrite && !released

  const load = useCallback(async (id: string) => {
    try {
      const d = await api.reports.get(id)
      setDetail(d)
      setContent(d.report.content ?? {})
      setDirty(false)
      setError('')
      if (d.template?.topics?.length) setActiveTopic(d.template.topics[0].id)
      setTemplateId(d.report.templateId ?? '')
      setExaminerId(d.report.professionalId ?? d.exam?.professionalId ?? '')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar laudo')
    }
  }, [])

  useEffect(() => { void load(reportId) }, [reportId, load])
  useEffect(() => { void api.templates.list().then((t) => setTemplates(t.items)).catch(() => undefined) }, [])
  useEffect(() => {
    void api.catalog.professionals().then((out) => setProfessionals(out.items)).catch(() => undefined)
    void api.catalog.equipments().then((out) => setEquipments(out.items)).catch(() => undefined)
  }, [])

  // O modelo escolhido na tela é o que define os tópicos — mesmo antes de salvar.
  const template = useMemo(
    () => templates.find((t) => t.id === templateId) ?? detail?.template,
    [templates, templateId, detail],
  )
  const topics = useMemo(() => template?.topics ?? [], [template])

  // Tudo o que um salvamento grava: texto, examinador e modelo.
  const pending = useMemo(() => ({
    content,
    professionalId: examinerId || undefined,
    ...(template && template.id !== detail?.report.templateId
      ? { templateId: template.id, title: template.printTitle, model: template.name }
      : {}),
  }), [content, examinerId, template, detail])

  // Texto digitado não se perde ao trocar de aba ou fechar a janela: o que
  // estiver por salvar é gravado na saída da tela.
  const exit = useRef({ dirty: false, pending })
  exit.current = { dirty: dirty && canEdit, pending }
  useEffect(() => {
    const flush = () => {
      if (exit.current.dirty) api.reports.saveOnExit(reportId, exit.current.pending)
      exit.current.dirty = false
    }
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [reportId])

  async function save() {
    if (!detail) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const out = await api.reports.save(detail.report.id, pending)
      setDetail(out)
      setDirty(false)
      setNotice('Laudo salvo (nova versão registrada).')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao salvar')
    } finally {
      setBusy(false)
    }
  }

  async function release() {
    if (!detail) return
    if (!confirm(`Liberar o laudo ${detail.report.number}? Depois de liberado, ele fica somente leitura.`)) return
    setBusy(true)
    setError('')
    try {
      if (dirty) {
        await api.reports.save(detail.report.id, pending)
      }
      const out = await api.reports.release(detail.report.id)
      setDetail(out)
      setContent(out.report.content ?? {})
      setDirty(false)
      setNotice('Laudo liberado.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao liberar')
    } finally {
      setBusy(false)
    }
  }

  async function reopen() {
    if (!detail) return
    setBusy(true)
    try {
      const out = await api.reports.reopen(detail.report.id)
      setDetail(out)
      setContent(out.report.content ?? {})
      setNotice('Laudo reaberto para edição.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao reabrir')
    } finally {
      setBusy(false)
    }
  }

  function switchTemplate(id: string) {
    if (!detail) return
    const tpl = templates.find((t) => t.id === id)
    if (!tpl) return
    const next: Record<string, string> = { ...content }
    for (const t of tpl.topics) {
      if (next[t.id] === undefined) next[t.id] = t.default ?? ''
    }
    setTemplateId(tpl.id)
    setContent(next)
    setDirty(true)
    setActiveTopic(tpl.topics[0]?.id ?? '')
    setError('')
    setNotice('')
  }

  async function openFrases() {
    if (!activeTopic) return
    try {
      const out = await api.phrases.list(activeTopic)
      setPhrases(out.items)
      setShowFrases(true)
    } catch {
      setError('falha ao carregar frases-padrão')
    }
  }

  function insertPhrase(p: Phrase) {
    setContent((c) => {
      const cur = c[activeTopic] ?? ''
      const next = cur.trim() ? cur.trimEnd() + ' ' + p.text : p.text
      return { ...c, [activeTopic]: next }
    })
    setDirty(true)
    setShowFrases(false)
  }

  async function toggleFav(p: Phrase) {
    try {
      const updated = await api.phrases.update({ ...p, favorite: !p.favorite })
      setPhrases((list) => list.map((x) => (x.id === p.id ? updated : x)))
    } catch {
      /* silencioso */
    }
  }

  // A imagem marcada entra no laudo impresso; a desmarcada continua no exame.
  async function toggleInReport(linkId: string, inReport: boolean) {
    try {
      await api.exams.updateLink(linkId, { inReport })
      setDetail((d) => d && { ...d, media: d.media.map((m) => (m.link.id === linkId ? { ...m, link: { ...m.link, inReport } } : m)) })
    } catch {
      setError('falha ao marcar a imagem')
    }
  }

  // A edição grava uma cópia: a foto original continua no exame, fora do laudo.
  async function saveEdited(index: number, blob: Blob, width: number, height: number) {
    const m = detail?.media[index]
    if (!m || !detail?.exam) return
    await saveEditedImage(detail.exam.id, { linkId: m.link.id, position: m.link.position, caption: m.link.caption }, blob, width, height)
    const fresh = await api.reports.get(reportId)
    setDetail((d) => d && { ...d, media: fresh.media }) // só as imagens: o texto em edição não é tocado
    setEditing(null)
    setViewing(null)
  }

  // O aparelho é do exame; sai no laudo impresso.
  async function changeEquipment(equipmentId: string) {
    if (!detail?.exam) return
    try {
      const exam = await api.exams.update({ ...detail.exam, equipmentId })
      setDetail((d) => d && { ...d, exam, equipment: equipments.find((e) => e.id === equipmentId)?.description ?? d.template?.equipment })
    } catch {
      setError('falha ao trocar o aparelho')
    }
  }

  async function saveCaption(linkId: string, caption: string) {
    try {
      await api.exams.updateLink(linkId, { caption })
      setNotice('Legenda atualizada.')
    } catch {
      setError('falha ao salvar legenda')
    }
  }

  async function removeMedia(linkId: string) {
    if (!confirm('Remover esta imagem do laudo? O arquivo original permanece em disco.')) return
    try {
      await api.exams.unlink(linkId)
      await load(reportId)
    } catch {
      setError('falha ao remover imagem')
    }
  }

  if (!detail) {
    return <div style={{ padding: 24, font: '400 12px/1.6 var(--font-mono)', color: 'var(--text-3)' }}>{error || 'Carregando laudo…'}</div>
  }

  const r = detail.report
  const examiner = (detail.examiner ?? {}) as Record<string, string>

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* toolbar */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button className="btn" onClick={onBack} style={{ height: 30 }}>‹ Laudos</button>
        <button className="btn" onClick={() => void save()} disabled={!canEdit || !dirty || busy} style={{ height: 30 }}>
          {busy ? '…' : dirty ? 'Salvar •' : 'Salvar'}
        </button>
        <button className="btn" onClick={() => void openFrases()} disabled={!canEdit || !activeTopic} style={{ height: 30 }}>
          Frases-padrão
        </button>
        <button className="btn" onClick={() => onOpenPreview(r.id)} style={{ height: 30 }}>
          Visualizar / Imprimir
        </button>
        <button className="btn" onClick={() => void api.reports.openPdf(r.id).catch(() => setError('falha ao gerar PDF'))} style={{ height: 30 }}>
          PDF
        </button>
        {released && (
          <button className="btn" onClick={() => setSending(true)} style={{ height: 30 }}>
            Enviar
          </button>
        )}
        {!released && canWrite && (
          <button className="btn btn-primary" onClick={() => void release()} disabled={busy} style={{ height: 30 }}>
            Liberar
          </button>
        )}
        {released && canWrite && (
          <button className="btn" onClick={() => void reopen()} disabled={busy}
            style={{ height: 30, borderColor: 'var(--accent-line)', background: 'var(--accent-soft)', color: 'var(--accent)', fontWeight: 600 }}>
            Reabrir laudo
          </button>
        )}
        <div style={{ flex: 1 }} />
        <select className="input" value={templateId} disabled={!canEdit}
          onChange={(e) => switchTemplate(e.target.value)} style={{ height: 30, width: 220 }}>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          {!templates.some((t) => t.id === templateId) && <option value={templateId}>{r.model}</option>}
        </select>
        <span className="badge" style={{
          background: released ? 'var(--ok-soft)' : 'var(--accent-soft)',
          color: released ? 'var(--ok)' : 'var(--accent)',
        }}>
          {released ? 'LIBERADO' : 'RASCUNHO'}
        </span>
      </div>

      {released && (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', background: 'var(--ok-soft)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ font: '500 12px/1.3 var(--font-sans)' }}>
            Liberado em {formatDateTimeBR(r.releasedAt)} · somente leitura
          </span>
        </div>
      )}

      {error && <div style={{ flex: 'none', padding: '8px 14px', background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
      {notice && <div style={{ flex: 'none', padding: '8px 14px', background: 'var(--surface-2)', color: 'var(--text-2)', fontSize: 12 }}>{notice}</div>}

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '300px 216px 1fr', minHeight: 0 }}>
        {/* dados + galeria */}
        <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
          <div style={{ flex: 'none', padding: '11px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 18 }}>
            <div>
              <div className="field"><span>Laudo Nº</span></div>
              <div style={{ font: '600 13px/1 var(--font-mono)' }}>{r.number}</div>
            </div>
            <div>
              <div className="field"><span>Criado em</span></div>
              <div style={{ font: '400 12px/1 var(--font-mono)', color: 'var(--text-2)' }}>{formatDateTimeBR(r.createdAt)}</div>
            </div>
          </div>

          <div style={{ flex: 'none', padding: '11px 12px', display: 'flex', flexDirection: 'column', gap: 10, borderBottom: '1px solid var(--border)' }}>
            <div className="field"><span>Paciente</span>
              <div style={{ font: '500 13px/1.3 var(--font-sans)' }}>{detail.patient?.name}</div>
              <div style={{ font: '400 11px/1.4 var(--font-mono)', color: 'var(--text-2)' }}>
                {formatDateBR(detail.patient?.birthDate)} · {formatAge(detail.patient?.birthDate, r.examDate)} · {detail.patient?.sex}
              </div>
            </div>
            <div className="field"><span>Médico examinador</span>
              {canEdit ? (
                <select className="input" value={examinerId} onChange={(e) => { setExaminerId(e.target.value); setDirty(true) }} style={{ width: '100%' }}>
                  <option value="">— selecione (sai no PDF) —</option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.crm ? ` · CRM ${p.crm_uf} ${p.crm}` : ''}</option>
                  ))}
                </select>
              ) : (
                <>
                  <div style={{ font: '400 12.5px/1.3 var(--font-sans)' }}>{examiner.name || '—'}</div>
                  {examiner.crm && <div style={{ font: '400 10.5px/1.4 var(--font-mono)', color: 'var(--text-3)' }}>CRM-{examiner.crm_uf} {examiner.crm}</div>}
                </>
              )}
            </div>
            <div className="field"><span>Exame</span>
              <div style={{ font: '400 12.5px/1.3 var(--font-sans)' }}>{detail.exam?.type} · {formatDateBR(r.examDate)}</div>
            </div>
            {equipments.length > 0 && (
              <div className="field"><span>Aparelho</span>
                {canEdit ? (
                  <select className="input" value={detail.exam?.equipmentId ?? ''} onChange={(e) => void changeEquipment(e.target.value)} style={{ width: '100%' }}>
                    <option value="">—</option>
                    {equipments.map((e) => <option key={e.id} value={e.id}>{e.description}</option>)}
                  </select>
                ) : (
                  <div style={{ font: '400 12.5px/1.3 var(--font-sans)' }}>{equipments.find((e) => e.id === detail.exam?.equipmentId)?.description ?? '—'}</div>
                )}
              </div>
            )}
            {detail.versions && detail.versions.length > 0 && (
              <div className="field"><span>Versões registradas</span>
                <div style={{ font: '400 10.5px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
                  {detail.versions.length} · última v{detail.versions[0].version} ({detail.versions[0].hash.slice(0, 10)}…)
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 'none', padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ font: '600 10.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
              Imagens · {detail.media.length}
            </span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 10, background: 'var(--surface-2)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
              {detail.media.map((m, i) => {
                const isPhoto = m.media.mime.startsWith('image/')
                const included = m.link.inReport !== false
                return (
                <div key={m.link.id} className="card" style={{ overflow: 'hidden', background: 'var(--media)' }}>
                  <div onClick={() => setViewing(i)} style={{ position: 'relative', cursor: 'pointer', opacity: isPhoto && !included ? 0.4 : 1 }}>
                  {isPhoto ? (
                    <MediaImage sha={m.media.sha256} alt={m.link.caption || 'captura'}
                      style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div className="media-surface" style={{ width: '100%', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cfdae1', font: '500 10.5px/1 var(--font-mono)' }}>
                      ▶ vídeo
                    </div>
                  )}
                  </div>
                  <div style={{ padding: 5, background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input className="input" defaultValue={m.link.caption} disabled={!canEdit} placeholder="Legenda da figura"
                      onBlur={(e) => { if (e.target.value !== m.link.caption) void saveCaption(m.link.id, e.target.value) }}
                      style={{ height: 24, fontSize: 10.5, padding: '0 6px' }} />
                    {canEdit && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {isPhoto ? (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 5, font: '400 10px/1 var(--font-sans)', color: 'var(--text-2)', cursor: 'pointer' }}>
                            <input type="checkbox" checked={included} onChange={(e) => void toggleInReport(m.link.id, e.target.checked)} />
                            no laudo
                          </label>
                        ) : <span />}
                        <button className="btn" onClick={() => void removeMedia(m.link.id)}
                          style={{ height: 20, fontSize: 10, padding: 0, color: 'var(--danger)', border: 'none', background: 'transparent' }}>
                          remover
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                )
              })}
            </div>
            {detail.media.length === 0 && (
              <div style={{ padding: '18px 12px', textAlign: 'center', font: '400 10.5px/1.5 var(--font-mono)', color: '#5d6f79' }}>
                Sem imagens vinculadas.<br />Capture na tela de Captura — o exame é o mesmo.
              </div>
            )}
          </div>
        </div>

        {/* tópicos */}
        <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ flex: 'none', padding: '9px 11px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ font: '600 10.5px/1 var(--font-sans)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Tópicos</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '5px 0' }}>
            {topics.map((t, i) => {
              const filled = (content[t.id] ?? '').trim().length
              return (
                <button key={t.id} onClick={() => setActiveTopic(t.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 11px',
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    background: activeTopic === t.id ? 'var(--accent-soft)' : 'transparent',
                    borderLeft: activeTopic === t.id ? '2px solid var(--accent-line)' : '2px solid transparent',
                  }}>
                  <span style={{ font: '500 9.5px/1.4 var(--font-mono)', color: 'var(--text-3)' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: filled ? 'var(--ok)' : 'var(--border-2)' }} />
                  <span style={{ flex: 1, font: '500 12.5px/1.3 var(--font-sans)' }}>{t.title}</span>
                  <span style={{ font: '400 9px/1.4 var(--font-mono)', color: filled ? 'var(--text-2)' : 'var(--text-3)' }}>{filled}</span>
                </button>
              )
            })}
          </div>
          <div style={{ flex: 'none', padding: '9px 11px', borderTop: '1px solid var(--border)', font: '400 10px/1.4 var(--font-mono)', color: 'var(--text-3)' }}>
            Modelo: {template?.name ?? r.model}
          </div>
        </div>

        {/* editor */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)' }}>
          <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            <span style={{ font: '600 11.5px/1 var(--font-sans)', letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
              {topics.find((t) => t.id === activeTopic)?.title ?? 'Conteúdo'}
            </span>
            <div style={{ flex: 1 }} />
            {dirty && <span style={{ font: '400 10.5px/1 var(--font-mono)', color: 'var(--accent)' }}>alterações não salvas</span>}
          </div>
          <textarea
            value={content[activeTopic] ?? ''}
            disabled={!canEdit}
            onChange={(e) => { setContent((c) => ({ ...c, [activeTopic]: e.target.value })); setDirty(true) }}
            style={{
              flex: 1, minHeight: 0, padding: '16px 18px', border: 'none', outline: 'none', resize: 'none',
              background: 'var(--surface)', color: 'var(--text)', font: '400 13.5px/1.75 var(--font-serif)',
              maxWidth: 980,
            }}
            placeholder="Texto do tópico…"
          />
          <div style={{ flex: 'none', padding: '8px 14px', borderTop: '1px solid var(--border)', font: '400 10.5px/1.4 var(--font-mono)', color: 'var(--text-3)' }}>
            {(content[activeTopic] ?? '').length} caracteres
          </div>
        </div>
      </div>

      {/* modal frases-padrão */}
      {showFrases && (
        <div onClick={() => setShowFrases(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(8,14,18,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div onClick={(e) => e.stopPropagation()} className="fade-in" style={{ width: 620, maxHeight: '76vh', background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 9, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 'none', padding: '12px 15px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'baseline', gap: 9 }}>
              <div style={{ font: '600 14px/1.2 var(--font-sans)' }}>Frases-padrão</div>
              <div style={{ font: '400 11.5px/1 var(--font-mono)', color: 'var(--text-2)' }}>
                {template?.name ?? r.model} › {topics.find((t) => t.id === activeTopic)?.title}
              </div>
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={() => setShowFrases(false)} style={{ height: 24, width: 24, padding: 0 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {phrases.map((p) => (
                <div key={p.id} className="fade-in" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 15px', borderBottom: '1px solid var(--border)' }}>
                  <button onClick={() => void toggleFav(p)}
                    style={{ width: 20, height: 20, flex: 'none', border: 'none', background: 'transparent', color: p.favorite ? 'var(--accent)' : 'var(--text-3)', fontSize: 14, cursor: 'pointer', padding: 0 }}>
                    {p.favorite ? '★' : '☆'}
                  </button>
                  <span onClick={() => insertPhrase(p)} style={{ flex: 1, font: '400 12.5px/1.4 var(--font-serif)', cursor: 'pointer' }}>
                    {p.text}
                  </span>
                </div>
              ))}
              {phrases.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', font: '400 11.5px/1.5 var(--font-mono)', color: 'var(--text-3)' }}>
                  Nenhuma frase para este tópico. Crie em Configurações › Frases-padrão.
                </div>
              )}
            </div>
            <div style={{ flex: 'none', padding: '10px 15px', borderTop: '1px solid var(--border)', font: '400 10.5px/1.4 var(--font-mono)', color: 'var(--text-3)' }}>
              Clique na frase para inserir no tópico ativo
            </div>
          </div>
        </div>
      )}

      {viewing !== null && detail.media[viewing] && (
        <MediaViewer
          items={detail.media.map((m) => ({ sha: m.media.sha256, mime: m.media.mime, caption: m.link.caption }))}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onEdit={canEdit ? setEditing : undefined}
        />
      )}
      {sending && <EnviarLaudo reportId={reportId} onClose={() => setSending(false)} />}
      {editing !== null && detail.media[editing] && (
        <ImageEditor
          sha={detail.media[editing].media.sha256}
          onCancel={() => setEditing(null)}
          onSave={(blob, w, h) => saveEdited(editing, blob, w, h)}
        />
      )}
    </div>
  )
}
